import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, fileSrc } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { X, Plus, ImagePlus, Mic, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import RecipientSelect, { useProjectRecipients } from "../RecipientSelect";

const filesToDataUrls = (files) =>
  Promise.all([...files].map((f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(f);
  })));

export const MESSAGE_CATEGORIES = ["General", "Design", "Budget", "Approvals", "Scheduling", "Other"];

const CATEGORY_COLORS = {
  General: "#A68A6B",
  Design: "#8C7154",
  Budget: "#C49A4A",
  Approvals: "#506B52",
  Scheduling: "#5C7A8C",
  Other: "#5C5651",
};

const toLocalDateKey = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const dateHeading = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return "Unknown date";
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const key = toLocalDateKey(iso);
  if (key === toLocalDateKey(today.toISOString())) return "Today";
  if (key === toLocalDateKey(yest.toISOString())) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" });
};

export default function CommunicationTab({ projectId, readOnly }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("");
  const [images, setImages] = useState([]);
  const [audio, setAudio] = useState(null); // { dataUrl, url }
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceHint, setVoiceHint] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const [dateFilter, setDateFilter] = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [openNote, setOpenNote] = useState(null);
  const markTimer = useRef(null);
  const { members, selected: recipients, setSelected: setRecipients } = useProjectRecipients(projectId);

  const load = () => api.get(`/projects/${projectId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [projectId]);

  const isUnread = (m) => m.sender_id !== user?.id && !(m.read_by || []).includes(user?.id);

  // Auto-mark unread notes as read shortly after viewing
  useEffect(() => {
    if (messages.some(isUnread)) {
      markTimer.current = setTimeout(async () => {
        try { await api.post(`/projects/${projectId}/messages/mark-read`); load(); } catch (e) {}
      }, 2500);
      return () => clearTimeout(markTimer.current);
    }
  }, [messages]);

  const presentCats = useMemo(
    () => MESSAGE_CATEGORIES.filter((c) => messages.some((m) => (m.category || "General") === c)),
    [messages]
  );

  const filtered = useMemo(() => {
    return messages
      .filter((m) => {
        if (dateFilter && toLocalDateKey(m.created_at) !== dateFilter) return false;
        if (catFilter !== "All" && (m.category || "General") !== catFilter) return false;
        return true;
      })
      .slice()
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // newest first
  }, [messages, dateFilter, catFilter]);

  // Group by date, most recent date first
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((m) => {
      const k = toLocalDateKey(m.created_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(m);
    });
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t && images.length === 0 && !audio) return;
    setTranscribing(!!audio);
    try {
      await api.post(`/projects/${projectId}/messages`, { text: t, category: category || "General", recipients, images, audio: audio?.dataUrl || null });
      setText("");
      setCategory("");
      setImages([]);
      clearAudio();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add note. Please try again.");
    } finally {
      setTranscribing(false);
    }
  };

  const clearAudio = () => {
    if (audio?.url) URL.revokeObjectURL(audio.url);
    setAudio(null);
    setRecSecs(0);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Recording isn't supported in this browser."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm", "audio/mp4"].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data.size) chunksRef.current.push(ev.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const r = new FileReader();
        r.onload = () => setAudio({ dataUrl: r.result, url: URL.createObjectURL(blob) });
        r.readAsDataURL(blob);
        stream.getTracks().forEach((tr) => tr.stop());
        clearInterval(recTimerRef.current);
        setRecording(false);
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      setRecSecs(0);
      recTimerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch (err) {
      toast.error("Microphone access was blocked.");
    }
  };
  const stopRecording = () => { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); };

  useEffect(() => () => { clearInterval(recTimerRef.current); }, []);

  const onPickImages = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const tooBig = [...files].some((f) => f.size > 6 * 1024 * 1024);
    if (tooBig) { toast.error("Each photo must be under 6MB"); e.target.value = ""; return; }
    try {
      const urls = await filesToDataUrls(files);
      setImages((prev) => [...prev, ...urls].slice(0, 8));
    } catch { toast.error("Could not read photo"); }
    e.target.value = "";
  };

  const fmtTime = (iso) =>
    new Date(iso).toLocaleString([], { hour: "2-digit", minute: "2-digit" });

  const fmtFull = (iso) =>
    new Date(iso).toLocaleString([], { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="max-w-3xl" data-testid="communication-tab">
      {/* Composer */}
      {!readOnly && (
      <form onSubmit={send} className="border border-[#EBE5E0] bg-white p-3 mb-4 space-y-3">
        <Textarea data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
          placeholder="Write a note… (Cmd/Ctrl + Enter to add)" rows={5}
          className="w-full min-h-[140px] rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B] resize-y text-sm leading-relaxed" />
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="note-image-previews">
            {images.map((src, i) => (
              <div key={i} className="relative w-20 h-20 border border-[#EBE5E0]">
                <img src={src} alt={`attachment ${i + 1}`} className="w-full h-full object-cover" />
                <button type="button" data-testid={`remove-note-image-${i}`}
                  onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))}
                  className="absolute -top-2 -right-2 bg-white border border-[#EBE5E0] text-[#5C5651] hover:text-[#9E4747] rounded-full p-0.5">
                  <X size={13} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        )}
        {audio && (
          <div className="flex items-center gap-3 border border-[#EBE5E0] bg-[#F9F8F6] p-2" data-testid="voice-memo-preview">
            <audio controls src={audio.url} className="h-9 flex-1" />
            <button type="button" data-testid="remove-voice-memo" onClick={clearAudio} className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={16} strokeWidth={1.5} /></button>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="message-category-select" className="w-[160px] rounded-none border-[#EBE5E0] focus:ring-0">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {MESSAGE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <RecipientSelect members={members} selected={recipients} setSelected={setRecipients} />
            <label data-testid="attach-photo-btn"
              className="inline-flex items-center gap-1.5 border border-[#EBE5E0] bg-white px-3 py-2 text-xs uppercase tracking-wider text-[#5C5651] hover:border-[#A68A6B] hover:text-[#A68A6B] cursor-pointer">
              <ImagePlus size={14} strokeWidth={1.5} className="text-[#A68A6B]" /> Photo
              <input data-testid="note-image-input" type="file" accept="image/*" multiple className="hidden" onChange={onPickImages} />
            </label>
            {!recording ? (
              <div className="relative">
                {(voiceHint || !!audio) && (
                  <div data-testid="voice-memo-hint"
                    className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 z-20 rounded-md bg-[#2B2622] text-white text-[11px] leading-snug normal-case tracking-normal px-3 py-2 shadow-lg">
                    Click <span className="font-semibold">Add Note</span> when done to auto-transcribe your message.
                    <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-[#2B2622]" />
                  </div>
                )}
                <button type="button" data-testid="record-voice-btn" onClick={startRecording}
                  onMouseEnter={() => setVoiceHint(true)} onMouseLeave={() => setVoiceHint(false)}
                  className="inline-flex items-center gap-1.5 border border-[#EBE5E0] bg-white px-3 py-2 text-xs uppercase tracking-wider text-[#5C5651] hover:border-[#A68A6B] hover:text-[#A68A6B]">
                  <Mic size={14} strokeWidth={1.5} className="text-[#A68A6B]" /> Voice memo
                </button>
              </div>
            ) : (
              <div className="relative">
                <div data-testid="voice-memo-hint"
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 z-20 rounded-md bg-[#2B2622] text-white text-[11px] leading-snug normal-case tracking-normal px-3 py-2 shadow-lg">
                  Click <span className="font-semibold">Add Note</span> when done to auto-transcribe your message.
                  <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-[#2B2622]" />
                </div>
                <button type="button" data-testid="stop-voice-btn" onClick={stopRecording}
                  className="inline-flex items-center gap-1.5 border border-[#9E4747] bg-[#9E4747] text-white px-3 py-2 text-xs uppercase tracking-wider animate-pulse">
                  <Square size={12} strokeWidth={1.5} className="fill-current" /> Stop · {String(Math.floor(recSecs / 60)).padStart(2, "0")}:{String(recSecs % 60).padStart(2, "0")}
                </button>
              </div>
            )}
          </div>
          <Button data-testid="send-message-btn" type="submit" disabled={transcribing}
            className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white px-6 uppercase tracking-wider text-xs">
            <Plus size={16} strokeWidth={1.5} className="mr-1" /> {transcribing ? "Transcribing…" : "Add Note"}
          </Button>
        </div>
      </form>
      )}

      {/* Search by date */}
      <div className="flex flex-wrap items-end gap-3 mb-4 border border-[#EBE5E0] bg-[#F9F8F6] p-4">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[11px] uppercase tracking-wider text-[#5C5651] block mb-1">Search by date</label>
          <Input data-testid="message-date-search" type="date" value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
        </div>
        {dateFilter && (
          <Button data-testid="clear-filters-btn" onClick={() => setDateFilter("")}
            className="rounded-none bg-transparent border border-[#EBE5E0] text-[#5C5651] hover:border-[#A68A6B] hover:text-[#A68A6B] uppercase tracking-wider text-xs">
            <X size={14} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Category tab sections */}
      {presentCats.length > 0 && (
        <div className="flex flex-wrap gap-1 border-b border-[#EBE5E0] mb-4" data-testid="category-tabs">
          {["All", ...presentCats].map((c) => {
            const active = catFilter === c;
            return (
              <button key={c} data-testid={`category-tab-${c}`} onClick={() => setCatFilter(c)}
                className={`uppercase tracking-wider text-xs px-4 py-2.5 border-b-2 transition-colors ${active ? "border-[#A68A6B] text-[#2B2622]" : "border-transparent text-[#5C5651] hover:text-[#A68A6B]"}`}>
                {c}
              </button>
            );
          })}
        </div>
      )}

      {/* Notes grouped by date, newest first */}
      <div className="space-y-8" data-testid="notes-list">
        {filtered.length === 0 && (
          <p className="text-center text-[#5C5651] text-sm py-12 border border-dashed border-[#D9CBBF] bg-white">
            {messages.length === 0 ? "No notes yet. Add the first one." : "No notes match your search."}
          </p>
        )}
        {groups.map(([dateKey, items]) => (
          <div key={dateKey} data-testid={`note-date-group-${dateKey}`}>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs uppercase tracking-[0.18em] text-[#A68A6B] whitespace-nowrap">{dateHeading(items[0].created_at)}</span>
              <span className="h-px bg-[#EBE5E0] flex-1" />
            </div>
            <div className="space-y-2">
              {items.map((m) => {
                const unread = isUnread(m);
                const cat = m.category || "General";
                const cColor = CATEGORY_COLORS[cat] || "#A68A6B";
                return (
                  <button key={m.id} data-testid="message-item" type="button"
                    onClick={() => setOpenNote(m)}
                    data-unread={unread ? "true" : "false"}
                    className={`block w-full text-left border p-4 transition-colors ${unread ? "bg-[#A68A6B] border-[#A68A6B] text-white hover:bg-[#8C7154]" : "bg-white border-[#EBE5E0] text-[#2B2622] hover:border-[#A68A6B]"}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] uppercase tracking-wider ${unread ? "text-white/90" : "text-[#A68A6B]"}`}>
                          {m.sender_name} · {m.sender_role}
                        </span>
                        <span data-testid="message-category-badge"
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5"
                          style={unread ? { background: "rgba(255,255,255,0.22)", color: "#fff" } : { background: cColor, color: "#fff" }}>
                          {cat}
                        </span>
                        {unread && (
                          <span data-testid="unread-dot" className="text-[10px] uppercase tracking-wider bg-white text-[#A68A6B] px-1.5 py-0.5">New</span>
                        )}
                      </div>
                      <span data-testid="message-timestamp" className={`text-[11px] ${unread ? "text-white/80" : "text-[#9b938b]"}`}>
                        {fmtTime(m.created_at)}
                      </span>
                    </div>
                    <p data-testid="note-preview" className="text-sm whitespace-pre-wrap leading-relaxed line-clamp-2">{m.text}</p>
                    {m.audio && (
                      <span data-testid="note-voice-badge" className={`inline-flex items-center gap-1 mt-1.5 text-[11px] ${unread ? "text-white/85" : "text-[#A68A6B]"}`}>
                        <Mic size={12} strokeWidth={1.75} /> Voice memo
                      </span>
                    )}
                    {m.images?.length > 0 && (
                      <div className="flex gap-1.5 mt-2" data-testid="note-preview-images">
                        {m.images.slice(0, 4).map((src, i) => (
                          <img key={i} src={fileSrc(src)} alt="" className="w-12 h-12 object-cover border border-black/10" />
                        ))}
                        {m.images.length > 4 && (
                          <span className={`text-[11px] self-end ${unread ? "text-white/80" : "text-[#9b938b]"}`}>+{m.images.length - 4}</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!openNote} onOpenChange={(o) => !o && setOpenNote(null)}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-lg" data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2B2622]">Note</DialogTitle>
            <DialogDescription className="sr-only">Full note details</DialogDescription>
          </DialogHeader>
          {openNote && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-[#A68A6B]">{openNote.sender_name} · {openNote.sender_role}</span>
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 text-white"
                  style={{ background: CATEGORY_COLORS[openNote.category || "General"] || "#A68A6B" }}>
                  {openNote.category || "General"}
                </span>
              </div>
              <p className="text-xs text-[#9b938b]">{fmtFull(openNote.created_at)}</p>
              <div className="h-px bg-[#EBE5E0]" />
              <p data-testid="note-full-text" className="text-sm text-[#2B2622] whitespace-pre-wrap leading-relaxed max-h-[40vh] overflow-y-auto">{openNote.text}</p>
              {openNote.audio && (
                <div className="pt-1" data-testid="note-dialog-audio">
                  <p className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#A68A6B] mb-1"><Mic size={12} strokeWidth={1.75} /> Voice memo{openNote.audio_transcript ? " · transcribed" : ""}</p>
                  <audio controls src={fileSrc(openNote.audio)} className="w-full h-10" />
                </div>
              )}
              {openNote.images?.length > 0 && (
                <div className="grid grid-cols-2 gap-2 pt-1" data-testid="note-dialog-images">
                  {openNote.images.map((src, i) => (
                    <a key={i} href={fileSrc(src)} target="_blank" rel="noopener noreferrer" data-testid={`note-dialog-image-${i}`}>
                      <img src={fileSrc(src)} alt={`attachment ${i + 1}`} className="w-full h-40 object-cover border border-[#EBE5E0] hover:opacity-90 transition-opacity" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
